import config, { benchRegistryAppId } from "../config";
import { DualStake, DSEnvironmentConfig } from "@myth-finance/dualstake-ts-sdk";
import { algod } from "./algo";
import algosdk, { EncodedTransaction, Transaction } from "algosdk";
import { shorten } from "./utils";
import { AlgorandClient } from "@algorandfoundation/algokit-utils";
import { useLifetimeStore } from "@/store/lifetime";
import {
  BenchInstanceClient,
  BenchRegistryClient,
} from "@myth-finance/bench-sdk";

const {
  network,
  dsRegistryAppId,
  dsSunsetRegistryAppId,
  tinymanAppId,
  arc59RouterAppId,
} = config;

export const defaultSender = config.sender;

export const algorand = AlgorandClient.fromClients({ algod });
const { lifetime } = useLifetimeStore.getState();
algorand.setDefaultValidityWindow(lifetime);

export const setDefaultValidityWindow = (validity: number) =>
  algorand.setDefaultValidityWindow(validity);

export const dSEnvironmentConfig: DSEnvironmentConfig = {
  algorand,
  sender: defaultSender,
  network,
  dsRegistryAppId,
  tinymanAppId,
  arc59RouterAppId,
};

export const dSSunsetEnvironmentConfig: DSEnvironmentConfig = {
  algorand,
  sender: defaultSender,
  network,
  dsRegistryAppId: dsSunsetRegistryAppId,
  tinymanAppId,
  arc59RouterAppId,
};

export const makeDSClient = (appId: bigint, account?: string) => {
  return DualStake.getContractAppClient({
    ...config,
    // @ts-ignore
    algorand,
    appId,
    sender: account ?? defaultSender,
  });
};

export const getBenchRegistryClient = () => {
  return new BenchRegistryClient({
    algorand,
    registryAppId: benchRegistryAppId,
    overrides: true,
  });
};

export const getBenchInstanceClient = (
  appId: bigint,
  registryAppId: bigint
) => {
  return new BenchInstanceClient({
    algorand,
    registryAppId,
    instanceAppId: appId,
    overrides: true,
  });
};

export function decodeTransactions(etxns: EncodedTransaction[]) {
  return etxns.map((e) => Transaction.from_obj_for_encoding(e));
}

export function verifySignedTransactions(
  unsigned: algosdk.Transaction[],
  signed: Uint8Array[]
): void {
  if (unsigned.length !== signed.length) {
    throw new Error(
      `Received incorrect number of transactions from wallet: Expected ${unsigned.length}, received ${signed.length}`
    );
  }
  for (let i = 0; i < signed.length; i++) {
    const stxn = algosdk.decodeSignedTransaction(signed[i]).txn;
    const utxn = unsigned[i];
    const sid = stxn.txID();
    const uid = utxn.txID();
    if (sid !== uid) {
      throw new Error(
        `Wallet mutated tranasction ${i + 1}: expected ID ${shorten(
          uid
        )} but received ${shorten(sid)}`
      );
    }
  }
}

export const makeMintTransactions = async (
  appId: bigint,
  account: string,
  algoAmount: number
  // TODO pass in pre-post txns
): Promise<algosdk.Transaction[]> => {
  const client = makeDSClient(appId, account);
  const etxns = await client.makeMintTransactions({ algoAmount });
  return etxns.map((e) => algosdk.Transaction.from_obj_for_encoding(e));
};

export const makeRedeemTransactions = async (
  appId: bigint,
  account: string,
  amount: number
): Promise<algosdk.Transaction[]> => {
  const client = makeDSClient(appId, account);
  const etxns = await client.makeRedeemTransactions({ amount });
  return etxns.map((e) => algosdk.Transaction.from_obj_for_encoding(e));
};

export const makeChangeNoderunnerTransactions = async (
  appId: bigint,
  account: string,
  noderunner: string
): Promise<algosdk.Transaction[]> => {
  const client = makeDSClient(appId, account);
  const etxns = await client.makeChangeNoderunnerTransactions({ noderunner });
  return etxns.map((e) => algosdk.Transaction.from_obj_for_encoding(e));
};

export const makeKeyregOnlineTransactions = async (
  appId: bigint,
  account: string,
  selectionKey: Uint8Array,
  votingKey: Uint8Array,
  spKey: Uint8Array,
  firstRound: number,
  lastRound: number,
  keyDilution: number,
  fee: number
): Promise<algosdk.Transaction[]> => {
  const client = makeDSClient(appId, account);
  const etxns = await client.makeKeyregOnlineTransactions({
    selectionKey,
    votingKey,
    spKey,
    firstRound,
    lastRound,
    keyDilution,
    fee,
  });
  return etxns.map((e) => algosdk.Transaction.from_obj_for_encoding(e));
};

export const makeKeyregOfflineTransactions = async (
  appId: bigint,
  account: string
): Promise<algosdk.Transaction[]> => {
  const client = makeDSClient(appId, account);
  const etxns = await client.makeKeyregOfflineTransactions({});
  return etxns.map((e) => algosdk.Transaction.from_obj_for_encoding(e));
};

export const makeWithdrawNoderunnerFeesTransactions = async (
  appId: bigint,
  account: string
): Promise<algosdk.Transaction[]> => {
  const client = makeDSClient(appId, account);
  const etxns = await client.makeWithdrawNoderunnerFeesTransactions({});
  return etxns.map((e) => algosdk.Transaction.from_obj_for_encoding(e));
};

export const makeUndoProtestTransactions = async (
  appId: bigint,
  account: string
): Promise<algosdk.Transaction[]> => {
  const client = makeDSClient(appId, account);
  const etxns = await client.makeUndoProtestTransactions({});
  return etxns.map((e) => algosdk.Transaction.from_obj_for_encoding(e));
};

export const makeProtestTransactions = async (
  appId: bigint,
  account: string,
  amount: number
): Promise<algosdk.Transaction[]> => {
  const client = makeDSClient(appId, account);
  const etxns = await client.makeProtestTransactions({ amount });
  return etxns.map((e) => algosdk.Transaction.from_obj_for_encoding(e));
};

export const makeWithdrawPlatformFeesTransactions = async (
  appId: bigint,
  account: string,
  amount: number
): Promise<algosdk.Transaction[]> => {
  const client = makeDSClient(appId, account);
  const etxns = await client.makeWithdrawPlatformFeesTransactions({ amount });
  return etxns.map((e) => algosdk.Transaction.from_obj_for_encoding(e));
};

export const makeChangeFeeAdminTransactions = async (
  appId: bigint,
  account: string,
  feeAdmin: string
): Promise<algosdk.Transaction[]> => {
  const client = makeDSClient(appId, account);
  const etxns = await client.makeChangeFeeAdminTransactions({ feeAdmin });
  return etxns.map((e) => algosdk.Transaction.from_obj_for_encoding(e));
};

export const makeChangeAdminTransactions = async (
  appId: bigint,
  account: string,
  admin: string
): Promise<algosdk.Transaction[]> => {
  const client = makeDSClient(appId, account);
  const etxns = await client.makeChangeAdminTransactions({ admin });
  return etxns.map((e) => algosdk.Transaction.from_obj_for_encoding(e));
};

export const makeSwapTransactions = async (
  tinymanAppId: number,
  tinymanEscrow: string,
  sender: string,
  algoAmount: number,
  outAssetId: number,
  assetAmt: number,
  needsOptin: boolean
): Promise<algosdk.Transaction[]> => {
  const suggestedParams = await algod.getTransactionParams().do();
  const optin = algosdk.makeAssetTransferTxnWithSuggestedParamsFromObject({
    from: sender,
    to: sender,
    assetIndex: outAssetId,
    amount: 0,
    suggestedParams,
  });

  const algoPay = algosdk.makePaymentTxnWithSuggestedParamsFromObject({
    from: sender,
    to: tinymanEscrow,
    amount: algoAmount,
    suggestedParams,
  });

  const doubleParams: algosdk.SuggestedParams = {
    ...suggestedParams,
    flatFee: true,
    fee: 3000,
  };

  const appCall = algosdk.makeApplicationCallTxnFromObject({
    from: sender,
    appIndex: tinymanAppId,
    appArgs: [
      new Uint8Array(Buffer.from("swap")),
      new Uint8Array(Buffer.from("fixed-output")),
      algosdk.encodeUint64(assetAmt),
    ],
    note: new TextEncoder().encode("mf"),
    foreignAssets: [0, outAssetId],
    accounts: [tinymanEscrow],
    suggestedParams: doubleParams,
    onComplete: algosdk.OnApplicationComplete.NoOpOC,
  });

  const txns = [needsOptin ? optin : null, algoPay, appCall].filter(
    (e) => e !== null
  );

  algosdk.assignGroupID(txns);

  return txns;
};

export async function makeCreateBenchTransactions(
  params: Omit<
    Parameters<BenchRegistryClient["makeCreateTxns"]>[0],
    "cancellable"
  >
): Promise<algosdk.Transaction[]> {
  const args = {
    cancellable: true,
    ...params,
  };
  const sdk = await getBenchRegistryClient();
  const etxns = await sdk.makeCreateTxns(args);
  return etxns.map((e) => algosdk.Transaction.from_obj_for_encoding(e));
}

export async function makeSetMetaTransactions(
  params: Parameters<BenchRegistryClient["makeSetMetaTxns"]>[0]
): Promise<algosdk.Transaction[]> {
  const sdk = await getBenchRegistryClient();
  const etxns = await sdk.makeSetMetaTxns(params);
  return etxns.map((e) => algosdk.Transaction.from_obj_for_encoding(e));
}
